import type { ReactNode } from "react";
import { UserRoundX } from "lucide-react";
import { OpenInToolboxCallout } from "@/components/toolbox/open-in-toolbox-callout";
import { useCanAnywhere } from "@/hooks/queries/use-toolbox-access";
import { useToolboxBridge } from "@/lib/toolbox/bridge";
import { ASK_TOOL_NAME } from "@/lib/toolbox/tools";
import { TOOLBOX_PAGE_TITLE } from "@/lib/toolbox/type-scale";

function Loading() {
  return (
    <div className="flex min-h-[300px] items-center justify-center" role="status" aria-live="polite">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
      <span className="sr-only">Loading...</span>
    </div>
  );
}

/** Why Product Q&A runs only in the app, for the open-in-Toolbox callout. */
export const ASK_OPEN_IN_TOOLBOX_REASON =
  "Product Q&A runs in SpecTree Toolbox: your own Claude Code reads the product's code on your computer to answer.";

/**
 * The gate of the Product Q&A page (epic PI-SPEC-SW-E310, feature
 * PI-SPEC-SW-1628, ER-1667 Package F): the `ask` Toolbox capability on at
 * least one project (an Admin everywhere). Unlike the FogBugz tools it needs
 * no saved key: answers come from the product's code on this computer, and
 * the page checks each product when the person picks it.
 *
 * - Without the capability (a Viewer, or no role): one plain sentence naming
 *   who can give a role.
 * - In a plain browser: the open-in-Toolbox callout, because the person's own
 *   Claude Code answers on their computer.
 *
 * The SpecTree API refuses the products read without `ask`, so this only
 * spares the person a page that could not work.
 */
export function RequireAskToolbox({ children }: { children: ReactNode }) {
  const bridge = useToolboxBridge();
  const { allowed, isLoading } = useCanAnywhere("ask");

  if (isLoading) return <Loading />;

  if (!allowed) {
    return (
      <div className="flex w-full flex-col gap-4 px-4 pb-6 pt-7">
        <h1 className={TOOLBOX_PAGE_TITLE}>{ASK_TOOL_NAME}</h1>
        <div className="glass-card flex items-start gap-3 rounded-[10px] px-[18px] py-4" data-testid="ask-no-role">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#1A1A1E]" aria-hidden="true">
            <UserRoundX className="h-4 w-4 text-muted-foreground" />
          </span>
          <p className="leading-relaxed text-muted-foreground">
            Your role does not include Product Q&amp;A. Ask an Admin or your Project Manager for a role on the
            product&apos;s project.
          </p>
        </div>
      </div>
    );
  }

  if (!bridge) {
    return (
      <div className="container mx-auto max-w-3xl px-4 py-6" data-testid="ask-open-in-toolbox">
        <OpenInToolboxCallout reason={ASK_OPEN_IN_TOOLBOX_REASON} />
      </div>
    );
  }

  return <>{children}</>;
}
